import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { MatchedCurator } from 'src/Redux/matchedCuratorsApi'
import { RootState } from 'src/Redux'

interface CampaignCuratorsData {
  curators: Record<string, MatchedCurator>
  manualMatchingBudget: number | null
}

interface SelectedCuratorsState {
  campaigns: Record<string, CampaignCuratorsData>
  activeCampaignId: string | null
}

const initialState: SelectedCuratorsState = {
  campaigns: {},
  activeCampaignId: null,
}

const getActiveCampaignData = (
  state: SelectedCuratorsState,
): CampaignCuratorsData | null => {
  if (!state.activeCampaignId) {
    return null
  }
  return state.campaigns?.[state.activeCampaignId] ?? null
}

const ensureActiveCampaignData = (
  state: SelectedCuratorsState,
): CampaignCuratorsData | null => {
  if (!state.activeCampaignId) {
    return null
  }
  if (!state.campaigns) {
    state.campaigns = {}
  }
  if (!state.campaigns[state.activeCampaignId]) {
    state.campaigns[state.activeCampaignId] = {
      curators: {},
      manualMatchingBudget: null,
    }
  }
  return state.campaigns[state.activeCampaignId]
}

export const selectedCuratorsSlice = createSlice({
  initialState,
  name: 'selectedCurators',
  reducers: {
    toggleCurator: (state, action: PayloadAction<MatchedCurator>) => {
      const data = ensureActiveCampaignData(state)
      if (!data) {
        return
      }
      const curatorId = String(action.payload.id)
      if (data.curators[curatorId]) {
        delete data.curators[curatorId]
      } else {
        data.curators[curatorId] = action.payload
      }
    },
    clearSelectedCurators: (state) => {
      if (state.activeCampaignId) {
        delete state.campaigns[state.activeCampaignId]
      }
    },
    setManualMatchingBudget: (state, action: PayloadAction<number>) => {
      const data = ensureActiveCampaignData(state)
      if (!data) {
        return
      }
      data.manualMatchingBudget = action.payload
    },
    setCampaignId: (state, action: PayloadAction<string>) => {
      state.activeCampaignId = action.payload
      const campaignData = state.campaigns?.[action.payload]
      if (campaignData) {
        campaignData.manualMatchingBudget = null
      }
    },
    filterSelectedCurators: (state, action: PayloadAction<number[]>) => {
      const data = getActiveCampaignData(state)
      if (!data) {
        return
      }
      const allowedIds = new Set(action.payload.map(String))
      const filteredCurators: Record<string, MatchedCurator> = {}

      Object.entries(data.curators).forEach(([id, curator]) => {
        if (allowedIds.has(id)) {
          filteredCurators[id] = curator
        }
      })

      data.curators = filteredCurators
    },
  },
})

export const {
  toggleCurator,
  clearSelectedCurators,
  setManualMatchingBudget,
  setCampaignId,
  filterSelectedCurators,
} = selectedCuratorsSlice.actions

export const selectedCuratorsReducer = selectedCuratorsSlice.reducer

export const selectSelectedCurators = (state: RootState): MatchedCurator[] => {
  const { activeCampaignId, campaigns } = state.selectedCurators
  if (!activeCampaignId || !campaigns?.[activeCampaignId]) {
    return []
  }
  return Object.values(campaigns[activeCampaignId].curators)
}

export const selectManualMatchingBudget = (state: RootState): number | null => {
  const { activeCampaignId, campaigns } = state.selectedCurators
  if (!activeCampaignId || !campaigns?.[activeCampaignId]) {
    return null
  }
  return campaigns[activeCampaignId].manualMatchingBudget
}
